import { z } from "zod";

import { BETA_MAP_ID_RE } from "../maps/betaMapId";
import { CHALLENGE_MAP_ID_RE } from "../maps/challengeMapId";
import { MAP_ID_RE } from "../maps/mapId";
import type { Move } from "./moves-events";
import { UNIT_KINDS } from "./primitives";

export const mapIdSchema = z
  .string()
  .regex(MAP_ID_RE, "Invalid mapId (expected YYYY-MM-DD-standard|premium).");

export const betaMapIdSchema = z
  .string()
  .regex(BETA_MAP_ID_RE, "Invalid mapId (expected beta/<slug>).");

export const challengeMapIdSchema = z
  .string()
  .regex(CHALLENGE_MAP_ID_RE, "Invalid mapId (expected challenge/<N>).");

const playableMapIdSchema = z.union([mapIdSchema, betaMapIdSchema, challengeMapIdSchema]);

export const monthKeySchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Invalid monthKey (expected YYYY-MM).");

const clientPlayerIdSchema = z.string().min(1).max(256);

export const optionalClientPlayerIdSchema = z.preprocess((value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}, z.string().min(1).max(256).optional());

const coordSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
});

const pathSchema = z.array(coordSchema).min(1).max(500);

const unitKindSchema = z.enum(UNIT_KINDS);

const moveSchema: z.ZodType<Move> = z.union([
  z.object({ type: z.literal("move"), unitId: z.string().min(1), path: pathSchema }),
  z.object({
    type: z.literal("join"),
    unitId: z.string().min(1),
    targetId: z.string().min(1),
    path: pathSchema,
  }),
  z.object({
    type: z.literal("attack"),
    unitId: z.string().min(1),
    targetId: z.string().min(1),
    path: pathSchema.optional(),
  }),
  z.object({
    type: z.literal("attack"),
    unitId: z.string().min(1),
    targetPos: coordSchema,
    path: pathSchema.optional(),
  }),
  z.object({ type: z.literal("wait"), unitId: z.string().min(1) }),
  z.object({ type: z.literal("endTurn") }),
  z.object({ type: z.literal("capture"), unitId: z.string().min(1) }),
  z.object({ type: z.literal("build"), pos: coordSchema, unitKind: unitKindSchema }),
  z.object({
    type: z.literal("load"),
    unitId: z.string().min(1),
    transportId: z.string().min(1),
    path: pathSchema,
  }),
  z.object({ type: z.literal("prepareUnload"), unitId: z.string().min(1) }),
  z.object({ type: z.literal("unload"), unitId: z.string().min(1), pos: coordSchema }),
]);

export const movesSchema = z.array(moveSchema).max(500);

export const submitTurnInputSchema = z.object({
  playerId: clientPlayerIdSchema,
  mapId: playableMapIdSchema,
  dayNumber: z.number().int().min(1).max(9_999),
  moves: movesSchema,
});

export const restartRunInputSchema = z.object({
  mapId: playableMapIdSchema,
  playerId: clientPlayerIdSchema,
});

export const undoTurnInputSchema = z.object({
  playerId: clientPlayerIdSchema,
  mapId: playableMapIdSchema,
  dayNumber: z.number().int().min(2).max(9_999),
});

export type SubmitTurnInput = z.infer<typeof submitTurnInputSchema>;
export type UndoTurnInput = z.infer<typeof undoTurnInputSchema>;
